import { memo, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslate } from "../../context/TranslationContext";
import useHomeController from "../../controllers/useHomeController";
import { useGetFunnelWithFilterQuery } from "../../redux/Apis/Funnel";
import Socials from "../socials";

const Footer = ({ className }) => {
  const { t } = useTranslate("footer");
  const lang = useSelector((state) => state.language.lang);
  const { general } = useSelector((state) => state.generalSlice);
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const { values, functions } = useHomeController("footer");
  let year = new Date().getFullYear();

  const tag =
    user?.role === "learner"
      ? "funnel_creator_interest"
      : "funnel_learner_entry";

  const {
    data: funnelData,
    refetch: refetchFunnel,
    isSuccess: funnelSuccess,
  } = useGetFunnelWithFilterQuery({ tag, lang });

  const funnel =
    funnelSuccess && funnelData?.data?.length
      ? (funnelData.data.find((item) => item.status) ?? null)
      : null;

  useEffect(() => {
    refetchFunnel();
  }, [tag]);

  useEffect(() => {
    const elfsightScript = document.createElement("script");
    elfsightScript.src = "https://static.elfsight.com/platform/platform.js";
    elfsightScript.async = true;
    document.body.appendChild(elfsightScript);

    const trustScript = document.createElement("script");
    trustScript.src =
      "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
    trustScript.async = true;
    trustScript.onload = () => {
      if (window.Trustpilot) {
        window.Trustpilot.loadFromElement(
          document.querySelector(".trustpilot-widget"),
          true,
        );
      }
    };
    document.body.appendChild(trustScript);

    return () => {
      document.body.removeChild(elfsightScript);
      document.body.removeChild(trustScript);
    };
  }, []);

  return (
    <>
      <p className="text-center text-muted fst-italic mb-0">
        {t("translateNote")}
      </p>

      <footer className={`pt-md-5 pt-4 pb-2 ${className ? className : ""}`}>
        <div className="container">
          <div className="row justify-content-between">
            <div className="col-xxl-7 col-xl-8">
              <div className="row">
                <div className="col-md-4 col-sm-6 my-3">
                  <h6 className="my-2 text-uppercase">{t("company")}</h6>
                  <ul className="ps-0 mb-0 mt-4">
                    <li>
                      <Link to="/about-us">{t("aboutUs")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/press">{t("press")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/contact-us">{t("contactUs")}</Link>
                    </li>
                  </ul>
                </div>

                <div className="col-md-4 col-sm-6 my-3">
                  <h6 className="my-2 text-uppercase">{t("categories")}</h6>
                  <ul className="ps-0 mb-0 mt-4">
                    {general?.categories?.map((item) => (
                      <li key={item._id} className="mt-2">
                        <a href="/classes">{item.name}</a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="col-md-4 col-sm-6 my-3">
                  <h6 className="my-2 text-uppercase">{t("help")}</h6>
                  <ul className="ps-0 mb-0 mt-4">
                    {isLoggedIn && (
                      <li className="mt-2">
                        <Link to="/profile/edit">{t("myAccount")}</Link>
                      </li>
                    )}
                    <li className="mt-2">
                      <Link to="/community">{t("communityForum")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/sustainable-planner">
                        {t("sustainablePlanner")}
                      </Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/calculators">{t("calculators")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/fitness-test">{t("fitnessTest")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/terms-and-condition">{t("terms")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/privacy-policy">{t("privacy")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/cookies-policy">{t("cookies")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/refund-policy">{t("refund")}</Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/physical-activity-disclaimer">
                        {t("disclaimer")}
                      </Link>
                    </li>
                    <li className="mt-2">
                      <Link to="/dei">{t("dei")}</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-lg-4 my-3">
              {funnel && (
                <>
                  <h6 className="my-2 text-uppercase">{funnel.title}</h6>
                  <p className="mt-4 p-sm">{funnel.message}</p>
                  <p className="mb-0 grey-text" style={{ fontSize: "14px" }}>
                    {funnel?.notes}
                  </p>

                  <div className="my-3">
                    <div className="position-relative">
                      <input
                        type="email"
                        placeholder={t("newsletterPlaceholder")}
                        className={`site-input pe-5 ${
                          values.error ? "is-invalid" : ""
                        }`}
                        value={values.email}
                        onChange={(e) => {
                          functions.setEmail(e.target.value);
                          if (values.error && values.error.includes("email")) {
                            functions.setError("");
                          }
                        }}
                        id="newsletter-email"
                        name="newsletter-email"
                        autoComplete="email"
                        disabled={values.isNewsletterLoading}
                      />
                      <button
                        className="transparent-btn right-icon"
                        onClick={functions.onSubmitSubscribe}
                      >
                        <span
                          style={{
                            display: values.isNewsletterLoading
                              ? "inline"
                              : "none",
                          }}
                        >
                          <Spinner
                            size="sm"
                            style={{ color: "var(--primaryColor)" }}
                          />
                        </span>
                        <span
                          style={{
                            display: values.isNewsletterLoading
                              ? "none"
                              : "inline",
                          }}
                        >
                          <i className="fas fa-angle-right" />
                        </span>
                      </button>
                    </div>

                    {values.error && (
                      <div className="invalid-feedback d-block">
                        {values.error}
                      </div>
                    )}

                    <label className="custom-checkbox-label mt-2 mb-0">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={values.agree}
                        onChange={(e) => {
                          functions.setAgree(e.target.checked);
                          if (
                            e.target.checked &&
                            values.error === t("funnerCheckError")
                          ) {
                            functions.setError("");
                          }
                        }}
                        disabled={values.isNewsletterLoading}
                      />
                      <span className="custom-checkbox">
                        <span className="checkmark" />
                      </span>
                      {t("newsletterAgree")}
                    </label>
                  </div>
                </>
              )}
              <Socials />
            </div>
          </div>
          <div className="row">
            <div className="col-12 py-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <p className="mb-0">
                  ©{year} Traineego — {t("rights")}
                </p>

                <Link to="/">
                  <img
                    src="../assets/images/logo.png"
                    alt="Traineego Logo"
                    className="img-fluid footer-logo"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
      <div
        className="elfsight-app-568b428c-a940-4269-92fb-dbdd89707676"
        data-elfsight-app-lazy
        style={{ marginTop: "20px" }}
      ></div>

      <div
        className="trustpilot-widget"
        data-locale="en-US"
        data-template-id="56278e9abfbbba0bdcd568bc"
        data-businessunit-id="66f6cf7d822deaf8e3b15882"
        data-style-height="52px"
        data-style-width="100%"
        style={{ marginTop: "20px" }}
      >
        <a
          href="https://www.trustpilot.com/review/traineego.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Trustpilot
        </a>
      </div>
    </>
  );
};

export default memo(Footer);
